import React, { createContext, useState, useEffect, useCallback, useRef } from "react";
import { getPatientMapV2 } from "../../utils/services/Patients/getPatientMapV2";
import { getNewMembers } from "../../utils/services/APP/NewMember/getNewMembers";
import { useValidToken } from "../../hooks/useValidToken";

export const PatientMapContext = createContext();

// El endpoint V2 no devuelve total. Si la página actual viene llena asumimos
// que hay al menos una más; si viene parcial, ahí terminó el dataset.
const estimateTotal = (data, page, pageSize) => {
  const len = Array.isArray(data) ? data.length : 0;
  if (len < pageSize) return page * pageSize + len;
  return (page + 1) * pageSize + 1;
};

export const PatientMapProvider = ({ children }) => {
  const [patientMap, setPatientMap] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const { isValid, authLoading } = useValidToken();

  // Paginación (page es base 0 para que case con DataGrid; al endpoint se le envía base 1)
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);

  // Filtros
  const [locationId, setLocationId] = useState(null);
  const [memberType, setMemberType] = useState(null);
  const [pcpName, setPcpName] = useState(null);
  const [gender, setGender] = useState(null);
  const [ageFrom, setAgeFrom] = useState(null);
  const [ageTo, setAgeTo] = useState(null);
  const [dateOfBirth, setDateOfBirth] = useState(null);
  const [memberId, setMemberId] = useState(null);
  const [memberFullName, setMemberFullName] = useState(null);
  const [filterAdmitted, setFilterAdmitted] = useState(null);
  const [filterDischarged, setFilterDischarged] = useState(null);
  const [filterEmergency, setFilterEmergency] = useState(null);
  const [filterHighRisk, setFilterHighRisk] = useState(null);
  // Solo pacientes activos (NUM_STATUS 1) — ENCENDIDO por defecto (pedido
  // 2026-10-01): el Patient Map arranca mostrando únicamente activos. OJO:
  // el SP_GET_PATIENT_MAP no tiene parámetro de status, así que el corte es
  // del FRONT sobre cada página ya traída — una página puede mostrar menos
  // filas que pageSize si trae inactivos. Cuando el backend agregue el
  // parámetro al SP, mover este filtro al request y borrar el corte local.
  const [onlyActive, setOnlyActive] = useState(true);

  const initialFetchDone = useRef(false);

  const fetchWithOverrides = useCallback(async (overrides = {}) => {
    const filters = {
      pageNumber: page + 1,
      pageSize,
      locationId,
      memberType,
      pcpName,
      gender,
      ageFrom,
      ageTo,
      dateOfBirth,
      memberId,
      memberFullName,
      filterAdmitted,
      filterDischarged,
      filterEmergency,
      filterHighRisk,
      ...overrides,
    };

    setLoading(true);
    try {
      const res = await getPatientMapV2(filters);
      const rows = Array.isArray(res?.data) ? res.data : [];
      // Corte a activos en cliente (ver nota sobre onlyActive). La estimación
      // de total/páginas se hace sobre la página CRUDA: la paginación sigue
      // siendo del servidor y no debe encogerse por el filtro local.
      const visible = onlyActive ? rows.filter((p) => Number(p.status) === 1) : rows;
      setPatientMap(visible.map((p) => ({ ...p, id: p.cipId })));
      setTotal(estimateTotal(rows, page, pageSize));
    } catch (err) {
      console.error("Error fetching patient map V2:", err);
      setPatientMap([]);
      setTotal(0);
    }
    setLoading(false);
  }, [
    page,
    pageSize,
    locationId,
    memberType,
    pcpName,
    gender,
    ageFrom,
    ageTo,
    dateOfBirth,
    memberId,
    memberFullName,
    filterAdmitted,
    filterDischarged,
    filterEmergency,
    filterHighRisk,
    onlyActive,
  ]);

  const fetchData = useCallback(async () => {
    await fetchWithOverrides();
  }, [fetchWithOverrides]);

  const fetchNewMembers = useCallback(async () => {
    const filters = {
      page: page + 1,
      pageSize,
      locationId,
      memberType,
      pcpName,
      gender,
      ageFrom,
      ageTo,
      memberId,
      memberFullName,
      filterAdmitted,
      filterDischarged,
      filterEmergency,
      filterHighRisk,
    };

    setLoading(true);
    try {
      const res = await getNewMembers(filters);
      setPatientMap(
        (res?.data || []).map((p) => ({
          ...p,
          id: p.cipId,
        }))
      );
      setTotal(res?.total ?? 0);
    } catch (err) {
      console.error("Error fetching new members:", err);
    }
    setLoading(false);
  }, [
    page,
    pageSize,
    locationId,
    memberType,
    pcpName,
    gender,
    ageFrom,
    ageTo,
    memberId,
    memberFullName,
    filterAdmitted,
    filterDischarged,
    filterEmergency,
    filterHighRisk,
  ]);

  const fetchAcaAging62To65 = useCallback(async () => {
    await fetchWithOverrides({
      memberType: "ACA",
      ageFrom: 62,
      ageTo: 65,
    });
  }, [fetchWithOverrides]);

  const fetchAcaAging65Plus = useCallback(async () => {
    await fetchWithOverrides({
      memberType: "ACA",
      ageFrom: 65,
      ageTo: null,
    });
  }, [fetchWithOverrides]);

  useEffect(() => {
    if (authLoading || !isValid) return;
    if (initialFetchDone.current) return;

    initialFetchDone.current = true;
    fetchData();
  }, [isValid, authLoading, fetchData]);

  return (
    <PatientMapContext.Provider
      value={{
        patientMap,
        total,
        loading,
        page,
        pageSize,
        setPage,
        setPageSize,

        // filtros
        locationId,
        setLocationId,
        memberType,
        setMemberType,
        pcpName,
        setPcpName,
        gender,
        setGender,
        ageFrom,
        setAgeFrom,
        ageTo,
        setAgeTo,
        dateOfBirth,
        setDateOfBirth,
        memberId,
        setMemberId,
        memberFullName,
        setMemberFullName,
        filterAdmitted,
        setFilterAdmitted,
        filterDischarged,
        setFilterDischarged,
        filterEmergency,
        setFilterEmergency,
        filterHighRisk,
        setFilterHighRisk,
        onlyActive,
        setOnlyActive,

        // fetchers
        fetchData,
        fetchAcaAging62To65,
        fetchAcaAging65Plus,
        fetchNewMembers,
      }}
    >
      {children}
    </PatientMapContext.Provider>
  );
};
